//@ts-nocheck
import { EditIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  Link,
  Stack,
  Text,
} from "@chakra-ui/react";
import { Formik } from "formik";
import { observer } from "mobx-react-lite";
import Pluralize from "pluralize";
import React from "react";
import { object, string } from "yup";
import { FormsetWrapper } from "../components/FormWrapper";
import { OverlayRouter } from "../components/OverlayRouter";
import { FFormContext } from "../primitives/forms/FormField";
import { FFormField } from "../primitives/landing-pages";
import { IEducationData, IEducationWorkHistoryStore } from "../stores";
import { orderAlphabetically, scrollToTop } from "../utils/functions";
import educationHistoryData from "./formData/educationHistory.json";
import { Formset } from "./forms/FormSet";

function startYearOptions(minYears = 40) {
  let currentYear = new Date().getFullYear();
  let startYear = currentYear - minYears;
  let options: Array<any> = [];
  for (let i = startYear; i < currentYear + 1; i++) {
    options = [...options, i];
  }
  return options.map((item) => ({ label: item, value: item.toString() }));
}

function endYearOptions(startYear?: any, minYears = 40) {
  let currentYear = new Date().getFullYear();
  let beginYear = startYear ? startYear : currentYear - minYears;
  let endYear = currentYear + 10;
  let options: Array<any> = [];
  for (let i = beginYear; i < endYear; i++) {
    options = [...options, i];
  }
  return options.map((item) => ({ label: item, value: item.toString() }));
}

const initialValue = {
  school: "",
  country: "",
  course: "",
  speciality: "",
  grade: "",
  degree: "",
  startYear: "",
  endYear: "",
};

const EducationFormRaw = (props: any) => {
  const {
    value,
    onSubmit,
    innerRef,
    showFormError,
    countries = [],
    educationData,
  } = props;
  // const [showFormError, setShowFormError] = React.useState(false);
  const { placeholders, validationData } = educationHistoryData;
  const degreeOptions = orderAlphabetically(
    educationData?.degree_data || [],
    "name"
  ).map(({ name, abbrev }) => ({
    label: `${name} (${abbrev})`,
    value: abbrev,
  }));
  const gradeOptions = educationData.grade_data.map((grade) => ({
    label: grade,
    value: grade,
  }));
  const specialityOptions = educationData.specialities.map((speciality) => {
    return { value: speciality, label: speciality };
  });

  const validationSchema = object().shape({
    school: string().required(validationData.school.required),
    country: string().required(validationData.country.required),
    course: string().required(validationData.course.required),
    speciality: string().required(validationData.speciality.required),
    grade: string().required(validationData.grade.required),
    degree: string().required(validationData.degree.required),
    startYear: string().required(validationData.startYear.required),
    endYear: string().required(validationData.endYear.required),
  });

  return (
    <Formik
      innerRef={innerRef}
      validationSchema={validationSchema}
      initialValues={{ ...initialValue, ...value }}
      onSubmit={onSubmit}
    >
      {({ values, handleSubmit }) => {
        return (
          <form onSubmit={handleSubmit}>
            <Stack spacing={6} width="100%" id="form-header-section">
              {showFormError && (
                <Box pt={2}>
                  <Alert rounded="lg" status="error">
                    <AlertIcon />
                    {educationHistoryData.formTitle.errorMessage}
                  </Alert>
                </Box>
              )}
              <FFormField name="school" />
              <FFormField name="country" type="select" options={countries} />
              <FFormField name="course" />
              <FFormField
                name="speciality"
                type="select"
                options={specialityOptions}
              />

              <Flex>
                <FFormField
                  name="startYear"
                  type="select"
                  options={startYearOptions()}
                />
                <Box w={6} h={6} />
                <FFormField
                  name="endYear"
                  type="select"
                  options={
                    values.startYear
                      ? endYearOptions(values.startYear)
                      : endYearOptions()
                  }
                />
              </Flex>
              <FFormField name="degree" type="select" options={degreeOptions} />
              <FFormField name="grade" type="select" options={gradeOptions} />
            </Stack>
          </form>
        );
      }}
    </Formik>
  );
};

export const EducationForm = observer(EducationFormRaw);

export const Education = ({
  field: education,
  onEdit,
}: {
  field: {
    school: string;
    country: string;
    course: string;
    degree: string;
    startYear: string;
    endYear: string;
    grade: string;
  };
  onEdit: () => any;
}) => {
  const { school, country, course, degree, startYear, endYear, grade } =
    education;

  const showEndYear = startYear !== endYear;
  const fieldsToExclude = ["educationProof"];
  const emptyFields = Object.keys(education)
    .filter((o) => !fieldsToExclude.includes(o))
    .map((x) => education[x])
    .filter((x) => !Boolean(x));
  return (
    <Box>
      {school && (
        <Flex justify="space-between" align="baseline">
          <Box flex={1}>
            <Text fontWeight="semibold">
              {degree}, {course}
            </Text>
            <Text>{school}</Text>

            {emptyFields.length > 0 && (
              <Link mt={2} onClick={onEdit} color="red.500" fontSize="xs">
                Some fields missing. Click to update
              </Link>
            )}
          </Box>

          <Stack ml={[3, 6]} align="center">
            <Button
              size="sm"
              leftIcon={<EditIcon />}
              variant="link"
              colorScheme="blue"
              onClick={onEdit}
            >
              {emptyFields.length > 0 ? "Update" : "Edit"}
            </Button>
            {emptyFields.length > 0 && (
              <Text
                fontSize="xs"
                py="1px"
                px={2}
                rounded="full"
                borderWidth="3xs"
                borderColor="red.500"
                color="red.500"
              >
                {Pluralize("error", emptyFields.length, true)}
              </Text>
            )}
          </Stack>
        </Flex>
      )}
    </Box>
  );
};

export type EducationHistoryType = {
  store: IEducationWorkHistoryStore;
  educationData: IEducationData;
  shouldDisplayButton: boolean;
  spacing?: number;
  onSubmit: any;
  displayType: string;
  buttonText: string;
  isDisabled?: boolean;
  loading: boolean;
  formsetDescription: string;
  lockedDescription: string;
  label: string;
  completed: boolean;
  formHeader: string;
  countries: any[];
  onBack?: any;
  textData?: {
    formTitle: { header: string };
    buttonText: { addText: string; save: string };
  };
  updateStore?: boolean;
  inContainer?: boolean;
};

const EducationHistory: React.FC<EducationHistoryType> = observer(
  ({
    store,
    spacing = 5,
    onSubmit,
    loading,
    educationData,
    label,
    countries,
    textData = educationHistoryData,
    onBack,
    inContainer,
    updateStore,
  }) => {
    React.useEffect(() => scrollToTop(), []);
    const [showFormError, setShowFormError] = React.useState(false);
    // const countries = rootStore.locationInfo.countries;
    const render = (
      <OverlayRouter>
        <FFormContext.Provider
          value={{ placeholders: educationHistoryData.placeholders } as any}
        >
          <Formset
            isLoading={loading}
            formsetValues={store.educations}
            Component={Education}
            onEdit={store.editEducation}
            onAdd={store.addEducation}
            addButtonText={textData.buttonText.addText}
            onSave={async (data: any) => {
              if (data.school && data.course) {
                store.saveEducation(data);
                if (onSubmit && !inContainer) {
                  await onSubmit(store.formValues, label);
                }
              }
            }}
            validateForm={(values) => {
              let {
                school,
                course,
                speciality,
                startYear,
                endYear,
                degree,
                grade,
              } = values;
              let isValid = Boolean(
                school &&
                  course &&
                  speciality &&
                  startYear &&
                  endYear &&
                  degree &&
                  grade
              );
              if (isValid) {
                setShowFormError(false);
                return isValid;
              } else {
                setShowFormError(true);
              }
            }}
            ComponentForm={EducationForm}
            formHeaderText={textData.formTitle.header}
            additionalFormProps={{ countries, showFormError, educationData }}
            currentFormIndex={store.currentEducationIndex}
            formDefaultValue={{ country: "Nigeria" }}
            onRemove={store.removeEducation}
            saveButtontext={textData.buttonText.save}
          />
        </FFormContext.Provider>
      </OverlayRouter>
    );
    return inContainer ? (
      <FormsetWrapper
        hideStep
        header={textData.formTitle.header}
        description={textData.formTitle.header}
        buttonText={textData.buttonText.save}
        buttonProps={{ isLoading: loading, onClick: onBack }}
        shouldDisplayButton={store.educationCompleted}
      >
        {render}
      </FormsetWrapper>
    ) : (
      render
    );
  }
);

export default EducationHistory;
